import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Heart, PawPrint, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const easeOut = [0.16, 1, 0.3, 1];
const STORAGE_KEY = "pawlink_thankyou_shown";

/**
 * Post-payment "Thank you for trusting PawLink" moment.
 *
 * Shown when the user lands on /home?paddle=success — Paddle overlay routes
 * here on checkout.completed. We keep it dead-simple: no timers, no CTAs
 * competing with content, just a warm confirmation.
 */
export default function ThankYouModal() {
    const { user, refresh } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const reduce = useReducedMotion();
    const [params, setParams] = useSearchParams();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        const isPostPaddle = params.get("paddle") === "success";
        if (!isPostPaddle) return;

        // Server-side gate wins over localStorage — protects across devices.
        const alreadyLocal = localStorage.getItem(STORAGE_KEY);
        let cancelled = false;

        (async () => {
            try {
                const { data: status } = await api.get("/paddle/status");
                if (cancelled) return;

                if (status?.thankyou_shown_at || alreadyLocal) {
                    // Clean up the URL param, no re-show
                    params.delete("paddle");
                    setParams(params, { replace: true });
                    return;
                }

                // Give the Paddle webhook a moment to land + refresh auth state
                setTimeout(async () => {
                    if (cancelled) return;
                    try { await refresh(); } catch { /* noop */ }
                    setOpen(true);
                }, 900);
            } catch { /* passive */ }
        })();

        return () => { cancelled = true; };
    }, [user, params, setParams, refresh]);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* noop */ }
        try { api.post("/paddle/mark-thankyou-seen"); } catch { /* passive */ }
        params.delete("paddle");
        setParams(params, { replace: true });
        setOpen(false);
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                key="ty-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: easeOut }}
                data-testid="thankyou-backdrop"
                className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-lg flex items-center justify-center p-4 sm:p-6"
                onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
            >
                <motion.div
                    key="ty-card"
                    initial={{ opacity: 0, scale: reduce ? 1 : 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: reduce ? 1 : 0.96 }}
                    transition={{ duration: 0.6, ease: easeOut }}
                    data-testid="thankyou-card"
                    className="relative w-full max-w-sm sm:max-w-md rounded-[28px] overflow-hidden bg-gradient-to-b from-[hsl(152,15%,10%)] to-[hsl(152,20%,5%)] border border-white/10 shadow-2xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="thankyou-title"
                >
                    <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-paw-terra/25 blur-3xl pointer-events-none" />
                    <div aria-hidden className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />

                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label={t("thankyou.close")}
                        data-testid="thankyou-close"
                        className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full bg-white/6 hover:bg-white/12 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
                    >
                        <X size={14} weight="bold" />
                    </button>

                    <div className="relative px-6 pt-10 pb-8 sm:pt-12 sm:pb-10 text-center">
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0, rotate: -8 }}
                            animate={{ scale: 1, opacity: 1, rotate: 0 }}
                            transition={{ delay: 0.2, duration: 0.7, ease: easeOut }}
                            className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-paw-terra to-rose-500 flex items-center justify-center shadow-lg shadow-paw-terra/40"
                        >
                            <Heart size={30} weight="fill" className="text-white" />
                        </motion.div>

                        <motion.h2
                            id="thankyou-title"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35, duration: 0.55 }}
                            data-testid="thankyou-title"
                            className="mt-5 sm:mt-6 font-display text-2xl sm:text-3xl leading-tight text-white"
                        >
                            {t("thankyou.title")}
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.55 }}
                            data-testid="thankyou-body"
                            className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed max-w-xs mx-auto"
                        >
                            {t("thankyou.body")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.55 }}
                            className="mt-6 sm:mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-500/25 px-4 py-2"
                        >
                            <PawPrint size={14} weight="fill" className="text-emerald-300" />
                            <span data-testid="thankyou-badge" className="text-[13px] font-semibold text-emerald-200">
                                {t("thankyou.badge")}
                            </span>
                        </motion.div>

                        <motion.button
                            type="button"
                            onClick={dismiss}
                            data-testid="thankyou-cta"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.85, duration: 0.5 }}
                            className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-6 py-3 text-sm sm:text-base font-semibold shadow-lg shadow-paw-terra/25 transition-all duration-300 hover:-translate-y-0.5"
                        >
                            {t("thankyou.cta")}
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
